import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { heroSlides } from "@/mocks/projects";

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (animating) return;
      setAnimating(true);
      setCurrent(index);
      setTimeout(() => setAnimating(false), 700);
    },
    [animating]
  );

  const next = useCallback(() => {
    goTo((current + 1) % heroSlides.length);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + heroSlides.length) % heroSlides.length);
  }, [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5500);
    return () => clearInterval(timer);
  }, [next]);

  const navigate = useNavigate();

  const handleScrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleSlideClick = () => {
    const slide = heroSlides[current];
    if (slide.projectId) navigate(`/projeler/${slide.projectId}`);
  };

  return (
    <section id="hero" className="relative w-full h-screen min-h-[600px] overflow-hidden">
      {/* Slides */}
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover object-top"
          />
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-20 flex flex-col items-center justify-center h-full text-center px-6 w-full">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 border border-white/40 text-white/90 text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-6 backdrop-blur-sm bg-white/10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4CAF50] inline-block" />
          22 Yıllık Deneyim
        </div>

        {/* Company Name */}
        <h1 className="text-white font-bold text-5xl md:text-6xl lg:text-7xl leading-tight mb-4 drop-shadow-lg">
          Güvenli Dizayn
        </h1>

        {/* Slide Title */}
        <p className="text-white/95 text-xl md:text-2xl font-light mb-3 max-w-2xl drop-shadow">
          {heroSlides[current].title}
        </p>

        {/* Slide Subtitle */}
        <p className="text-white/75 text-base md:text-lg mb-10 max-w-xl">
          {heroSlides[current].subtitle}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 items-center">
          <button
            onClick={() => handleScrollTo("#projeler")}
            className="flex items-center gap-2 bg-[#4CAF50] text-white font-semibold px-8 py-3.5 rounded-full hover:bg-[#388E3C] transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
          >
            Projeleri İncele
            <i className="ri-arrow-right-line" />
          </button>
          <button
            onClick={() => handleScrollTo("#iletisim")}
            className="flex items-center gap-2 border-2 border-white/70 text-white font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm backdrop-blur-sm"
          >
            İletişime Geç
          </button>
        </div>
      </div>

      {/* Prev / Next Arrows */}
      <button
        onClick={prev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white transition-all duration-200 cursor-pointer"
        aria-label="Önceki"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        onClick={next}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white transition-all duration-200 cursor-pointer"
        aria-label="Sonraki"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      {/* Dot Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2.5">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current
                ? "w-8 h-2 bg-[#4CAF50]"
                : "w-2 h-2 bg-white/50 hover:bg-white/80"
            }`}
            aria-label={`Slayt ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 z-30 flex flex-col items-center gap-1 text-white/60">
        <span className="text-xs uppercase tracking-widest" style={{ writingMode: "vertical-rl" }}>Kaydır</span>
        <i className="ri-arrow-down-line text-sm animate-bounce" />
      </div>
    </section>
  );
}
